/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {Component, memo} from 'react';
import SwipeableBottomSheet from 'react-swipeable-bottom-sheet';
import classnames from 'classnames';
import {dlsZIndex} from '@ion/dls/src/theme/theme';
import Hidden from '../../layout/Hidden';
import {SideSheetView} from '../../types/types';
import {isSheetTableView} from './helpers';
import MobileSideSheetArrow from './MobileSideSheetArrow';
import styles from './SideSheet.module.scss';

interface SideSheetTypes {
  children?: Component;
  menuItems: {
    showSideSheet: boolean;
    page: string;
  }[];
  selectedIndex: number;
  sideSheetStatus?: null | {
    activeSideSheet: null | string;
    desktopSideSheetOpen: boolean;
    mobileSideSheetOpen: boolean;
  };
  toggleSideSheet?: Function;
  showFindNearByPopup?: boolean;
  findButtonStatus?: boolean;
  isInitialized?: Boolean;
  isLoaded?: Boolean;
  sideSheetView?: SideSheetView;
  isMobileFilterActive?: boolean;
  sideSheetMobileSwipeUp?: Function;
  fullHeight?: boolean;
}

interface BottomSheetStylesMap {
  [key: string]: string;
}

/**
 * @SideSheet
 *
 * Defines the SideSheet Component. Props determine whether or not the sheet should
 * be rendered (based on settings in nav reducer), tracks its open/closed state
 * (and half state on mobile), and conditionally assigns classes that determine where
 * the sheet should be rendered based on the user's device per the DLS.
 *
 * Then renders any children passed to it
 *
 * ```typescript
 *
 * <SideSheet sideSheetOpen={isSideSheetOpen}
 *   menuItems={menuItems}
 *   selectedIndex={selectedIndex}>
 *   {sideSheetContent}
 *  </SideSheet>
 *
 * ```
 */
export const SideSheet = ({
  children,
  isInitialized,
  isLoaded,
  menuItems,
  selectedIndex,
  toggleSideSheet,
  showFindNearByPopup,
  findButtonStatus,
  sideSheetMobileSwipeUp,
  sideSheetView,
  sideSheetStatus = null,
  isMobileFilterActive = false,
  fullHeight = false
}: SideSheetTypes) => {
  const showSideSheet = menuItems[selectedIndex]?.showSideSheet;
  const {activeSideSheet, desktopSideSheetOpen, mobileSideSheetOpen} = sideSheetStatus || {};

  function handleTapDown() {
    toggleSideSheet({
      page          : activeSideSheet,
      desktopStatus : false,
      mobileStatus  : !mobileSideSheetOpen
    });
  };

  // if sheet is grabbed from the middle and proper
  // open status is not set, set it manually
  function handleIndex(index) {
    if (!index) {
      toggleSideSheet({page: activeSideSheet, desktopStatus: false, mobileStatus: false});
    } else {
      toggleSideSheet({page: activeSideSheet, desktopStatus: false, mobileStatus: true});
    }
  }

  const showTrackingBottomSheet = isMobileFilterActive || !isSheetTableView(sideSheetView);

  return (showSideSheet ? (
    <>
      <Hidden show='mobile'>
        {(!showFindNearByPopup || findButtonStatus) && (isInitialized && isLoaded && showTrackingBottomSheet) && (
          <SwipeableBottomSheet overflowHeight={!fullHeight ? 34 : 105}
            style={{zIndex: dlsZIndex('sideSheetSwipeableBottomSheet')}}
            shadowTip={false}
            bodyStyle={{
              height               : fullHeight ? 'calc(100vh - 105px)' : 'calc(100vh - 124px)',
              borderTopLeftRadius  : (!mobileSideSheetOpen && fullHeight) ? '10px' : 0,
              borderTopRightRadius : (!mobileSideSheetOpen && fullHeight) ? '10px' : 0,
              overflow             : (!mobileSideSheetOpen && fullHeight) ? 'initial' : 'auto'
            }}
            topShadow={!mobileSideSheetOpen}
            open={mobileSideSheetOpen}
            overlay={false}
            swipeableViewsProps={{
              onChangeIndex  : handleIndex,
              containerStyle : {
                borderTopLeftRadius  : (!mobileSideSheetOpen && fullHeight) ? '10px' : 0,
                borderTopRightRadius : (!mobileSideSheetOpen && fullHeight) ? '10px' : 0
              }
            }}>
            <>
              {(!mobileSideSheetOpen && fullHeight) && (
                <MobileSideSheetArrow handleTapDown={handleTapDown}
                  mobileSidesheet
                  fullHeight={fullHeight}
                  sideSheetMobileSwipeUp={sideSheetMobileSwipeUp}/>
              )}
              <div>
                {children}
              </div>
            </>
          </SwipeableBottomSheet>
        )}
        {showFindNearByPopup && !findButtonStatus && (isInitialized && isLoaded) && (
          <SwipeableBottomSheet overflowHeight={34}
            style={{zIndex: dlsZIndex('sideSheetSwipeableBottomSheet')}}
            shadowTip={false}
            bodyStyle={{
              height               : 'calc(100vh - 285px)',
              borderTopLeftRadius  : '10px',
              borderTopRightRadius : '10px',
              overflow             : 'scroll',
              maxHeight            : '380px'
            }}
            open={mobileSideSheetOpen}
            swipeableViewsProps={{
              onChangeIndex  : handleIndex,
              containerStyle : {
                borderTopLeftRadius  : '10px',
                borderTopRightRadius : '10px'
              }
            }}>
            <>
              <MobileSideSheetArrow handleTapDown={handleTapDown} />
              <div>
                {children}
              </div>
            </>
          </SwipeableBottomSheet>
        )}
      </Hidden>
      <Hidden show='desktop'>
        <div className={classnames(
          desktopSideSheetOpen ? styles.sideSheetOpen : null,
          styles.sideSheet)}>
          {children}
        </div>
      </Hidden>
    </>
  ) : null);
};

export default memo(SideSheet);
